<template>
  <div
    :id="id"
    class="server-table relative bg-white rounded-lg m-4 overflow-hidden"
  >
    <div
      v-show="loading"
      class="dt-progress absolute inset-x-0 top-0 h-0.5 bg-blue-600"
    />
    <header class="flex flex-wrap gap-4 justify-between items-start px-6 py-4">
      <h2 class="text-[22px] font-medium text-black leading-tight">
        {{ title }}
      </h2>
      <div class="flex flex-wrap gap-4 items-end">
        <button
          v-if="cardConfig"
          class="px-4 py-2.5 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
          data-cy="server-table-view-toggle"
          @click="viewMode = viewMode === 'cards' ? 'table' : 'cards'"
        >
          {{ viewMode === "cards" ? "Table view" : "Card view" }}
        </button>
        <DatatableFilter
          v-for="filter in globalFilters"
          :key="filter.name"
          :filter="filter"
          :datatable-id="id"
          :version="1"
          @refresh-table="onFilterRefresh"
        />
        <button
          class="relative flex gap-2 justify-center items-center px-5 py-2.5 text-gray-900 whitespace-nowrap bg-white rounded-lg border border-solid border-gray-300 hover:bg-gray-50"
          data-cy="server-table-filter-btn"
          @click="columnSearchOpen = !columnSearchOpen"
        >
          Filter
          <span
            v-if="filterCount"
            class="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-900 rounded-full"
          >
            {{ filterCount }}
          </span>
        </button>
        <button
          class="flex gap-2 justify-center items-center px-5 py-2.5 rounded-lg border text-white bg-blue-700 border-blue-700 hover:bg-blue-800 whitespace-nowrap"
          data-cy="server-table-download"
          @click="exportReport"
        >
          <i class="fas fa-download" />
          <span>{{
            exportState === "queued"
              ? "Queued ✓"
              : exportState === "failed"
                ? "Export failed"
                : "Download"
          }}</span>
        </button>
      </div>
    </header>

    <section
      v-if="appliedFilters.length"
      class="flex flex-wrap gap-1.5 items-center py-3 px-6 border-t text-sm"
    >
      <span class="text-gray-900">Filtering by:</span>
      <button
        v-for="chip in appliedFilters"
        :key="chip.key"
        class="flex gap-2 items-center px-4 py-2 text-xs font-medium text-blue-800 bg-blue-100 hover:bg-blue-200 rounded-lg"
        @click="removeChip(chip)"
      >
        {{ chip.label }}
        <span aria-hidden="true">✕</span>
      </button>
    </section>

    <!-- Card mode: sort + filters collapse into single controls above the
         list (per the TB-2201 spike decision) driving the SAME sorting /
         colSearch state the table's <thead> controls use. -->
    <section v-if="viewMode === 'cards'" class="flex flex-col gap-2 px-4 pb-2">
      <div class="flex gap-2">
        <select
          class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white"
          data-cy="card-sort"
          :value="sorting[0] ? `${sorting[0].id}:${sorting[0].desc}` : ''"
          @change="onCardSort(($event.target as HTMLSelectElement).value)"
        >
          <option value="">Sort…</option>
          <template v-for="col in sortableColumns" :key="col.data">
            <option :value="`${col.data}:false`">
              {{ col.title || col.data }} ↑
            </option>
            <option :value="`${col.data}:true`">
              {{ col.title || col.data }} ↓
            </option>
          </template>
        </select>
        <button
          class="flex-none px-4 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300"
          data-cy="card-filters-toggle"
          @click="columnSearchOpen = !columnSearchOpen"
        >
          Filters {{ columnSearchOpen ? "▴" : "▾" }}
        </button>
      </div>
      <div
        v-if="columnSearchOpen"
        class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
        data-cy="card-filters-panel"
      >
        <label
          v-for="col in searchableColumns"
          :key="col.data"
          class="text-xs text-gray-600"
        >
          {{ col.title || col.data }}
          <input
            :value="colSearch[col.data] || ''"
            type="search"
            placeholder="Search"
            class="mt-0.5 w-full border border-gray-300 rounded px-2 py-1.5 text-[13px] text-gray-900"
            :data-cy="`cardsearch-${col.data}`"
            @input="
              table.setColumnSearch(
                col.data,
                ($event.target as HTMLInputElement).value
              )
            "
          />
        </label>
      </div>
    </section>

    <CardList
      v-if="viewMode === 'cards' && cardConfig"
      :rows="cardRows"
      :config="cardConfig"
      :loading="loading"
      :error="error"
      :total="recordsFiltered"
      :page-size="pagination.pageSize"
      :can-load-more="canLoadMore"
      @load-more="table.loadMore"
      @retry="table.refetch()"
    />

    <div
      v-if="viewMode === 'table'"
      ref="scrollBody"
      class="dt-scroll"
      :style="{ maxHeight: scrollMaxHeight }"
    >
      <table class="w-full text-left border-collapse">
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col.data"
              class="dt-th align-bottom text-[12px] font-semibold uppercase tracking-wide whitespace-nowrap text-left py-4 px-4 first:pl-6"
              :class="[
                col.className,
                {
                  'cursor-pointer': col.orderable !== false,
                  'dt-sticky dt-sticky-head': col.data === 'actions'
                }
              ]"
              @click="col.orderable !== false && table.toggleSort(col.data)"
            >
              <span class="flex gap-1 items-center">
                {{ col.title === undefined ? col.data : col.title }}
                <span
                  v-if="col.orderable !== false"
                  class="dt-sort"
                  :class="sortState(col)"
                  >{{ sortGlyph(col) }}</span
                >
              </span>
              <div
                v-if="columnSearchOpen"
                class="mt-2 min-w-[150px] font-normal normal-case"
                @click.stop
              >
                <DatatableFilter
                  v-if="columnFilter(col)"
                  :filter="columnFilter(col)"
                  :datatable-id="id"
                  :version="1"
                  no-label
                  @refresh-table="onFilterRefresh"
                />
                <input
                  v-else-if="col.searchable !== false"
                  :value="colSearch[col.data] || ''"
                  type="search"
                  placeholder="Search"
                  class="w-full border border-gray-300 rounded px-2 py-1 text-[13px] text-gray-900"
                  :data-cy="`colsearch-${col.data}`"
                  @input="
                    table.setColumnSearch(
                      col.data,
                      ($event.target as HTMLInputElement).value
                    )
                  "
                />
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="error && !loading">
            <td
              :colspan="visibleColumns.length"
              class="py-10 text-center text-gray-600"
            >
              <p class="mb-2">Something went wrong loading this report.</p>
              <p class="mb-3 text-xs text-gray-400">{{ error }}</p>
              <button
                class="px-4 py-2 text-sm text-white bg-blue-700 hover:bg-blue-800 rounded-lg"
                data-cy="server-table-retry"
                @click="table.refetch()"
              >
                Retry
              </button>
            </td>
          </tr>
          <tr v-else-if="loading && !rows.length">
            <td
              :colspan="visibleColumns.length"
              class="py-10 text-center text-gray-400"
            >
              Loading…
            </td>
          </tr>
          <tr v-else-if="!rows.length">
            <td
              :colspan="visibleColumns.length"
              class="py-12 text-center text-gray-500"
            >
              No matching records found
            </td>
          </tr>
          <tr
            v-for="row in rows"
            v-else
            :id="row.DT_RowId != null ? String(row.DT_RowId) : undefined"
            :key="row.DT_RowId ?? JSON.stringify(row)"
            class="dt-row hover:bg-[#f6f9fe]"
            :class="row.DT_RowClass"
          >
            <td
              v-for="col in visibleColumns"
              :key="col.data"
              :data-col="col.data"
              class="text-[14px] text-gray-900 py-4 px-4 first:pl-6 align-middle"
              :class="[
                col.className,
                { 'dt-sticky dt-sticky-body': col.data === 'actions' }
              ]"
              v-html="row[col.data] ?? ''"
            />
          </tr>
        </tbody>

        <tfoot v-if="hasAggregates" class="dt-foot">
          <tr>
            <td
              v-for="col in visibleColumns"
              :key="`agg_${col.data}`"
              class="py-2.5 px-4 first:pl-6 whitespace-nowrap align-middle"
              :class="[
                col.footerClassName,
                { 'dt-sticky dt-sticky-foot': col.data === 'actions' }
              ]"
            >
              <template v-if="aggregates[col.data] != null">
                <span
                  class="block text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                >
                  {{ col.aggregateTitle || "Total" }}
                </span>
                <span
                  class="text-[14px] text-gray-900"
                  v-html="aggregates[col.data]"
                />
              </template>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <footer
      v-if="viewMode === 'table'"
      class="flex flex-wrap gap-4 justify-between items-center px-6 py-4 text-sm text-gray-600"
    >
      <div class="flex gap-4 items-center">
        <span>
          Showing
          <span class="font-semibold text-gray-900">{{ range.start }}</span>
          -
          <span class="font-semibold text-gray-900">{{ range.end }}</span>
          of
          <span class="font-semibold text-gray-900">{{ range.total }}</span>
        </span>
        <label class="flex gap-2 items-center mb-0">
          <select
            :value="pagination.pageSize"
            class="border border-gray-300 rounded-lg py-1 pl-3 pr-8"
            @change="
              table.setPageSize(
                Number(($event.target as HTMLSelectElement).value)
              )
            "
          >
            <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">
              {{ n }} items
            </option>
          </select>
        </label>
        <PageJumpInput
          v-if="pageCount > 1"
          :total-pages="pageCount"
          :current-page="pagination.pageIndex + 1"
          @jump="(p: number) => table.setPageIndex(p - 1)"
        />
      </div>
      <nav
        v-if="pageCount > 1"
        class="flex gap-1 items-center"
        aria-label="pagination"
      >
        <button
          class="dt-page"
          :disabled="!table.getCanPreviousPage() || loading"
          @click="table.previousPage()"
        >
          ‹
        </button>
        <button
          v-if="pageWindow[0] > 0"
          class="dt-page"
          @click="table.setPageIndex(0)"
        >
          1
        </button>
        <span v-if="pageWindow[0] > 1" class="px-1 text-gray-400">…</span>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="dt-page"
          :class="{ 'dt-page-active': p === pagination.pageIndex }"
          @click="table.setPageIndex(p)"
        >
          {{ p + 1 }}
        </button>
        <span
          v-if="pageWindow[pageWindow.length - 1] < pageCount - 2"
          class="px-1 text-gray-400"
          >…</span
        >
        <button
          v-if="pageWindow[pageWindow.length - 1] < pageCount - 1"
          class="dt-page"
          @click="table.setPageIndex(pageCount - 1)"
        >
          {{ pageCount }}
        </button>
        <button
          class="dt-page"
          :disabled="!table.getCanNextPage() || loading"
          @click="table.nextPage()"
        >
          ›
        </button>
      </nav>
    </footer>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import { EventBus } from "@/lib/event_bus";
import { isFalsy } from "@/lib/helpers";
import { extractProps, renderVueComponent } from "@/lib/renderVueComponent.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import { resolveDatatableComponent } from "@/components/datatableComponentRegistry";
import { bypassesFilterDefaults } from "@/components/datatableDefaults";
import PageJumpInput from "@/components/PageJumpInput.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import DatatableFilter from "@/Reports/DatatableFilter.vue";
import CardList, { type CardConfig } from "./CardList.vue";
import {
  type ServerColumn,
  type SortSpec,
  useServerTable
} from "./useServerTable";

interface Filter {
  type: string;
  name: string;
  label?: string;
  visible?: boolean;
  default_value?: string | boolean;
}
interface Config {
  columns: ServerColumn[];
  order?: (number | string)[][];
  vueComponents?: Array<{
    rootId: string;
    component: any;
    options?: { store?: any };
  }>;
}

const props = withDefaults(
  defineProps<{
    id: string;
    source: string;
    config: Config;
    filters?: Filter[];
    title?: string;
    cardConfig?: CardConfig;
    /** Warm the next page after each fetch (doubles endpoint load; off by default). */
    prefetch?: boolean;
  }>(),
  { filters: () => [], title: "", cardConfig: undefined, prefetch: false }
);

const allColumns = computed(() => props.config.columns || []);

function defaultSorting(): SortSpec[] {
  const first = props.config.order?.[0];
  if (!first) return [];
  const idx = Number(first[0]);
  const col = allColumns.value[idx];
  if (!col) return [];
  return [{ id: col.data, desc: String(first[1]).toLowerCase() === "desc" }];
}

// Filter values live in the URL as `<id>_<name>` (written by DatatableFilter);
// the server expects the bare `<name>`. Drill-through params pass through.
const filterParamName = (name: string) =>
  props.id ? `${props.id}_${name}` : name;

// A filter's default_value used to be applied by its own DatatableFilter in
// created(). Column filters render inside the column-search panel, which is
// closed on load, so those instances never mount and their defaults were
// never applied — a bare-path report load queried the whole table. ServerTable
// seeds them itself instead, for every filter, mounted or not.
//
// Rules are mirrored from Reports/DatatableFilter.vue setInitialFilters (the
// authority): a URL param wins over the default, and the drill-through
// sentinel suppresses defaults entirely so a dashboard link's own predicate
// is not overwritten.
//
// Seeded values are held here as well as written to the URL because the write
// is a router navigation that lands a tick or more later, while the first
// request is built during mount. Reading them makes that request carry the
// default, so a bare path produces one filtered request rather than an
// unfiltered one followed by a filtered one. Each entry is dropped as soon as
// its value is readable from the URL, after which the URL is the single
// source of truth and clearing a filter cannot resurrect its default.
const seededDefaults: Record<string, string> = {};

// The bare `<name>` counts as a value here as well as the prefixed one, so a
// drill-through link that carries its own value never has a default written
// over the top of it.
const filtersNeedingDefaults = () => {
  const q = getQueryVariables() as Record<string, string>;
  if (bypassesFilterDefaults(q)) return [];
  return props.filters.filter(
    (f) =>
      f.visible !== false &&
      !isFalsy(f.default_value) &&
      isFalsy(q[filterParamName(f.name)]) &&
      isFalsy(q[f.name])
  );
};

const seedFilterDefaults = () => {
  const pending = filtersNeedingDefaults();
  pending.forEach((f) => {
    seededDefaults[f.name] = String(f.default_value);
  });
  return Promise.all(
    pending.map(async (f) => {
      const param = filterParamName(f.name);
      try {
        await updateQueryParam(param, f.default_value, true);
      } catch {
        // The seeded value still reaches the request; only the URL is behind.
      }
      const q = getQueryVariables() as Record<string, string>;
      if (!isFalsy(q[param])) delete seededDefaults[f.name];
    })
  );
};

// Runs during setup, before the first render and therefore before anything
// can build a request or a filter can hydrate itself from the URL.
void seedFilterDefaults();

const filterValue = (f: Filter, q: Record<string, string>) =>
  q[filterParamName(f.name)] ?? seededDefaults[f.name];

const requestFilterParams = () => {
  const q = getQueryVariables() as Record<string, string>;
  const out: Record<string, string> = {};
  props.filters.forEach((f) => {
    const val =
      q[filterParamName(f.name)] ?? q[f.name] ?? seededDefaults[f.name];
    if (val != null && val !== "") out[f.name] = val;
  });
  ["customer_id", "address_id"].forEach((k) => {
    if (q[k]) out[k] = q[k];
  });
  return out;
};

const table = useServerTable({
  source: props.source,
  columns: () => allColumns.value,
  initialSort: defaultSorting(),
  prefetch: props.prefetch,
  extraParams: requestFilterParams,
  onAfterFetch: async () => {
    setAppliedFilters();
    // Hydrate after the new rows are in the DOM — otherwise querySelectorAll
    // finds nothing and the in-cell components never mount.
    await nextTick();
    hydrateCells();
    runCreatedCells();
    setScrollHeight();
  }
});
const {
  rows,
  cardRows,
  error,
  recordsFiltered,
  aggregates,
  loading,
  sorting,
  colSearch,
  pagination,
  visibleColumns,
  pageCount,
  canLoadMore
} = table;

// Card config: explicit prop wins; otherwise derived from the columns' own
// cardRole metadata — the contract the blessed component ships.
const cardConfig = computed<CardConfig | undefined>(() => {
  if (props.cardConfig) return props.cardConfig;
  const byRole = (role: string) =>
    allColumns.value.filter((c) => c.cardRole === role).map((c) => c.data);
  const primary = byRole("primary")[0];
  if (!primary) return undefined;
  return {
    primary,
    secondary: byRole("secondary"),
    meta: byRole("meta"),
    status: byRole("status")[0],
    actions: byRole("actions")[0]
  };
});

// Card mode is a second renderer over the same table state. The breakpoint
// drives the mode reactively (resize flips it); the header toggle overrides
// until the next breakpoint change.
const { type: screenType } = useBreakpoints();
const modeFor = (t: string) =>
  cardConfig.value && t === "mobile" ? "cards" : "table";
const viewMode = ref<"table" | "cards">(modeFor(screenType.value));
watch(screenType, (t) => (viewMode.value = modeFor(t)));
// Mode switches restart from page one so card accumulation and the table's
// pager never disagree about what "the current page" means.
watch(viewMode, () => table.resetAccumulation());

const exportState = ref<"idle" | "queued" | "failed">("idle");
const columnSearchOpen = ref(false);

// A filter belongs to a column when its name matches that column's
// filterName (or data). Those render in the column header; the rest are
// toolbar ("global") filters — mirroring DatatableV3.
const columnFilterName = (col: ServerColumn) => col.filterName || col.data;
const columnFilter = (col: ServerColumn) =>
  props.filters.find(
    (f) => f.visible !== false && f.name === columnFilterName(col)
  );
const globalFilters = computed(() => {
  const colNames = new Set(visibleColumns.value.map(columnFilterName));
  return props.filters.filter(
    (f) => f.visible !== false && !colNames.has(f.name)
  );
});

const sortableColumns = computed(() =>
  visibleColumns.value.filter(
    (c) => c.orderable !== false && c.data !== "actions"
  )
);
const searchableColumns = computed(() =>
  visibleColumns.value.filter(
    (c) => c.searchable !== false && c.data !== "actions"
  )
);
const onCardSort = (value: string) => {
  if (!value) {
    table.setSort(null);
    return;
  }
  const [id, desc] = value.split(":");
  table.setSort([{ id, desc: desc === "true" }]);
};
const hasAggregates = computed(() => Object.keys(aggregates.value).length > 0);

const range = computed(() => {
  const total = recordsFiltered.value;
  if (!total) return { start: 0, end: 0, total: 0 };
  const start = pagination.pageIndex * pagination.pageSize + 1;
  const end = Math.min(start + rows.value.length - 1, total);
  return {
    start: start.toLocaleString(),
    end: end.toLocaleString(),
    total: total.toLocaleString()
  };
});

// Windowed page numbers around the current page (0-based indexes).
const pageWindow = computed(() => {
  const total = Math.max(pageCount.value, 1);
  const cur = pagination.pageIndex;
  const start = Math.max(0, Math.min(cur - 2, total - 5));
  const end = Math.min(total - 1, Math.max(cur + 2, 4));
  const pages: number[] = [];
  for (let i = Math.max(0, start); i <= end; i++) pages.push(i);
  return pages;
});

const sortGlyph = (col: ServerColumn) => {
  const s = sorting.value[0];
  if (!s || s.id !== col.data) return "↕";
  return s.desc ? "▼" : "▲";
};
const sortState = (col: ServerColumn) => {
  const s = sorting.value[0];
  const active = !!s && s.id === col.data;
  return { "opacity-90": active, "opacity-40": !active };
};

// Applied-filter chips ("Filtering by: …"), mirroring DatatableV3. Filter
// values live in the URL as `<id>_<name>` (written by DatatableFilter).
type Chip = {
  key: string;
  name: string;
  kind: "filter" | "search";
  label: string;
};
const appliedFilters = ref<Chip[]>([]);

const columnTitle = (dataKey: string) => {
  const col = allColumns.value.find((c) => c.data === dataKey);
  if (!col) return dataKey;
  return col.title === undefined ? col.data : col.title || col.data;
};

// A filter chip shows just its value (option labels for selects, the range for
// dates) — no name prefix — matching DatatableV3.
const chipLabel = (filter: Filter, value: string) => {
  const opts = (filter as any).options as
    Array<{ label: string; value: unknown }> | undefined;
  if (Array.isArray(opts) && opts.length) {
    const byVal = new Map(opts.map((o) => [String(o.value), o.label]));
    return String(value)
      .split(",")
      .map((v) => byVal.get(v) ?? v)
      .join(", ");
  }
  return value;
};

const setAppliedFilters = () => {
  const q = getQueryVariables() as Record<string, string>;
  const filterChips: Chip[] = props.filters
    .filter((f) => {
      const v = filterValue(f, q);
      return v != null && v !== "";
    })
    .map((f) => ({
      key: `filter:${f.name}`,
      name: f.name,
      kind: "filter",
      label: chipLabel(f, filterValue(f, q)!)
    }));
  // Per-column text searches also show as chips, e.g. Department: "5".
  const searchChips: Chip[] = Object.entries(colSearch)
    .filter(([, v]) => v)
    .map(([dataKey, v]) => ({
      key: `search:${dataKey}`,
      name: dataKey,
      kind: "search",
      label: `${columnTitle(dataKey)}: "${v}"`
    }));
  appliedFilters.value = [...filterChips, ...searchChips];
};

const filterCount = computed(() => appliedFilters.value.length);

const removeChip = async (chip: Chip) => {
  if (chip.kind === "filter") {
    const filter = props.filters.find((f) => f.name === chip.name);
    // Drop the seeded fallback too, or clearing a defaulted filter would
    // immediately re-apply its default on the next request.
    delete seededDefaults[chip.name];
    // Await the URL write before refreshing, as DatatableV3.removeFilter and
    // DatatableFilter.handleFilterChange both do: the refetch has to be built
    // from the cleared query string, not the one it is replacing.
    await updateQueryParam(filterParamName(chip.name), null, true);
    if (filter) EventBus.$emit("clear-filter", filter);
    onFilterRefresh();
  } else {
    table.clearColumnSearch(chip.name);
  }
};

const onFilterRefresh = () => table.resetAccumulation();

// Refetch with fresh server data — the contract Quotes.vue calls via $refs on
// the `refresh-quote-datatable` event (e.g. after editing a quote in-cell).
const refreshTable = () => table.refetch();
defineExpose({ refreshTable });

// Bounded scroll body with sticky header + footer (DataTables scrollY). Fill
// the viewport down to the pagination footer; recomputed on mount/resize/draw.
const scrollBody = ref<HTMLElement | null>(null);
const scrollMaxHeight = ref("70vh");
const setScrollHeight = () => {
  const el = scrollBody.value;
  if (!el) return;
  const top = el.getBoundingClientRect().top;
  const reserve = 96; // pagination footer + card bottom margin
  scrollMaxHeight.value = `${Math.max(240, Math.round(window.innerHeight - top - reserve))}px`;
};

// Mount the in-cell Vue components the server emits as
// <div id="<rootId>" class="vue-component" data="<json props>">, reusing the
// exact registry + renderVueComponent path DatatableV3 uses.
const hydrateCells = () => {
  (props.config.vueComponents || []).forEach(
    ({ rootId, component, options = {} }) => {
      const resolved = resolveDatatableComponent(component, rootId);
      if (!resolved) return;
      document.querySelectorAll(`#${rootId}`).forEach((node) => {
        const cellProps = extractProps(node as HTMLElement, options);

        const wrapped = defineComponent({
          setup() {
            return () => h(resolved, { ...cellProps });
          }
        });
        renderVueComponent(node, wrapped, { store: options?.store });
      });
    }
  );
};

// DataTables `createdCell` parity: run each column's createdCell hook against
// its rendered <td> so cell-level wiring (e.g. click-to-navigate on the Job /
// Invoice cells) works. Guarded per-cell so re-hydration doesn't double-bind.
const runCreatedCells = () => {
  const cols = allColumns.value.filter(
    (c) => typeof c.createdCell === "function"
  );
  const container = scrollBody.value;
  if (!cols.length || !container) return;
  rows.value.forEach((row) => {
    const rowId = row.DT_RowId;
    if (rowId == null) return;
    const tr = container.querySelector(`#${CSS.escape(String(rowId))}`);
    if (!tr) return;
    cols.forEach((col) => {
      const td = tr.querySelector<HTMLElement>(`td[data-col="${col.data}"]`);
      if (!td || td.dataset.createdCell) return;
      col.createdCell!(td, row[col.data], row);
      td.dataset.createdCell = "1";
    });
  });
};

let exportInFlight = false;
const exportReport = async () => {
  // Mirror DatatableV3: POST the current filters to the report's create action
  // (background export job). The report path is the source without `.json`.
  if (exportInFlight || exportState.value === "queued") return;
  exportInFlight = true;
  const target = props.source.split("?")[0].replace(/\.json$/, "");
  const body = new URLSearchParams();
  // The export must cover exactly what the screen shows: the same filters AND
  // drill-through params (customer_id/address_id) the data requests send.
  Object.entries(requestFilterParams()).forEach(([k, v]) => body.set(k, v));
  const token =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";
  try {
    const res = await fetch(target, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json"
      },
      body
    });
    // An expired session 302s to the login page and the follow lands on
    // res.ok — a redirected or non-JSON response is a failure, not a queue.
    const contentType = res.headers?.get?.("content-type") || "";
    const queued =
      !res.redirected &&
      (res.status === 204 || (res.ok && contentType.includes("json")));
    exportState.value = queued ? "queued" : "failed";
  } catch {
    exportState.value = "failed";
  } finally {
    exportInFlight = false;
    setTimeout(() => (exportState.value = "idle"), 4000);
  }
};

onMounted(() => {
  table.fetch();
  nextTick(setScrollHeight);
  window.addEventListener("resize", setScrollHeight);
});
onBeforeUnmount(() => window.removeEventListener("resize", setScrollHeight));
</script>

<style scoped>
/* Match DatatableV3: light-blue uppercase headers with indigo text. */
.dt-th {
  background-color: #e1effe;
  color: #233876;
}
.dt-sort {
  font-size: 10px;
  line-height: 1;
}
/* Archived quotes render muted, like the report's voided-row. */
.voided-row {
  opacity: 0.5;
}
.dt-page {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
}
.dt-page:hover:not(:disabled) {
  background: #f3f4f6;
}
.dt-page:disabled {
  opacity: 0.4;
  cursor: default;
}
.dt-page-active,
.dt-page-active:hover {
  background: #1d4ed8;
  color: #fff;
}
/* Indeterminate refetch bar (shows while data reloads; previous rows stay). */
.dt-progress {
  animation: dt-progress 1.1s ease-in-out infinite;
  transform-origin: left center;
}
@keyframes dt-progress {
  0% {
    transform: scaleX(0.05);
    opacity: 0.5;
  }
  50% {
    transform: scaleX(1);
    opacity: 1;
  }
  100% {
    transform: scaleX(0.05);
    opacity: 0.5;
  }
}
/* Pinned/frozen actions column (right edge), matching the report's fixed column. */
.dt-sticky {
  position: sticky;
  right: 0;
  z-index: 2;
}
.dt-sticky::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 10px;
  transform: translateX(-100%);
  box-shadow: inset -10px 0 8px -8px rgba(15, 23, 42, 0.18);
  pointer-events: none;
}
.dt-sticky-head {
  background: #e1effe;
}
.dt-sticky-body {
  background: #fff;
}
tbody tr:hover .dt-sticky-body {
  background: #f6f9fe;
}
/* Bounded scroll body with sticky header (top) + aggregate footer (bottom). */
.dt-scroll {
  overflow: auto;
}
.server-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
}
.server-table tfoot {
  border-top: 1px solid #eef2f7;
}
.server-table tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #fff;
}
/* Actions-column corner cells sit above both the sticky row and column. */
.server-table thead th.dt-sticky,
.server-table tfoot td.dt-sticky {
  z-index: 4;
}
.dt-sticky-foot {
  background: #fff;
}
/* PageJumpInput ships items-end + Bootstrap form-control for its DataTables
   home; here the footer centers everything and the input matches the
   page-size select's box. !important is required to outrank Tailwind's
   utilities, which are all !important (tailwind.config.js important: true). */
:deep(.page-jump) {
  align-items: center !important;
}
:deep(.page-jump .page-jump-input) {
  width: 4.5rem !important;
  height: 34px !important;
  min-height: 34px !important;
  padding: 0.25rem 0.5rem !important;
  border: 1px solid rgb(209 213 219) !important;
  border-radius: 0.5rem !important;
  margin: 0 !important;
}
</style>
